import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";

/**
 * s221 Lane 1, mechanism 8 — ONE THING OPEN AT A TIME (rule 66).
 *
 * "Opening a contact card, a writing box or an address block closes whatever else is open on that
 * screen; what was closed is kept, and clicking its box brings it back." Reade, s212: "you need to be
 * able to open only one at a time."
 *
 * A screen wraps itself in `OnePanelProvider`; every panel asks `usePanel(key)` whether it is the open
 * one. Opening a panel makes it THE open one, so the previous one closes. Nothing is lost by closing:
 * the panels hold no answers — the screen does — so a closed card reopens with what was typed.
 *
 * Keys are the screen's choice; the convention is `<rowId>:<what>` ("r1:guardian", "r1:note").
 * Outside a provider each panel keeps its own open state, so a piece still works on its own.
 */

/**
 * s233 (D-233.54(2)) — IS ANYTHING OPEN? Every provider, and every panel hook used without one, reports whether it has a
 * card or panel open; the shell hides "More below" while any is (every chapter: every card and panel opens through here).
 */
const openReporters = new Set<number>();
const listeners = new Set<() => void>();
let nextReporter = 0;
function setReported(id: number, open: boolean) {
  if (open === openReporters.has(id)) return;
  if (open) openReporters.add(id); else openReporters.delete(id);
  listeners.forEach((l) => l());
}
function useReportOpen(open: boolean) {
  const [id] = useState(() => ++nextReporter);
  useEffect(() => { setReported(id, open); }, [id, open]);
  useEffect(() => () => setReported(id, false), [id]);
}
/** True while any card or panel the interview opens is open. */
export function useAnyPanelOpen(): boolean {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => openReporters.size > 0,
    () => false,
  );
}

type Ctx = { openKey: string | null; setOpenKey: (key: string | null) => void };
const OnePanelContext = createContext<Ctx | null>(null);

export function OnePanelProvider({ children }: { children: ReactNode }) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  useReportOpen(openKey !== null);
  const value = useMemo(() => ({ openKey, setOpenKey }), [openKey]);
  return <OnePanelContext.Provider value={value}>{children}</OnePanelContext.Provider>;
}

export type Panel = { isOpen: boolean; open: () => void; close: () => void; toggle: () => void };

export function usePanel(key: string): Panel {
  const ctx = useContext(OnePanelContext);
  const [localOpen, setLocalOpen] = useState(false);
  useReportOpen(!ctx && localOpen); // inside a provider, the provider reports

  const isOpen = ctx ? ctx.openKey === key : localOpen;
  const open = useCallback(() => (ctx ? ctx.setOpenKey(key) : setLocalOpen(true)), [ctx, key]);
  // Closing only clears the key if this panel is still the open one — a stale close from a panel
  // that was already replaced must not shut the panel that replaced it.
  const close = useCallback(
    () => (ctx ? ctx.setOpenKey(ctx.openKey === key ? null : ctx.openKey) : setLocalOpen(false)),
    [ctx, key],
  );
  const toggle = useCallback(() => (isOpen ? close() : open()), [isOpen, open, close]);
  return { isOpen, open, close, toggle };
}

/**
 * The same rule for a list of panels keyed at render time (one per row), where a hook per panel
 * cannot be called in a loop: `isOpen(key)` / `toggle(key)` against the provider's one open key.
 */
export function usePanels() {
  const ctx = useContext(OnePanelContext);
  const [localKey, setLocalKey] = useState<string | null>(null);
  useReportOpen(!ctx && localKey !== null); // inside a provider, the provider reports
  const openKey = ctx ? ctx.openKey : localKey;
  const setKey = ctx ? ctx.setOpenKey : setLocalKey;
  return {
    isOpen: (key: string) => openKey === key,
    open: (key: string) => setKey(key),
    close: (key: string) => setKey(openKey === key ? null : openKey),
    toggle: (key: string) => setKey(openKey === key ? null : key),
  };
}
